import React from "react";

import { useQuill } from "react-quilljs";
import Textarea from "./Textarea";
import Button from "./Button";

const moduleOptions = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ size: ["small", false, "large", "huge"] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ color: [] }, { background: [] }],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const QuillComponent = ({
  value,
  handleChange,
  placeholder,
}: {
  value: string;
  handleChange: (value: string) => void;
  placeholder: string;
}) => {
  const { quill, quillRef } = useQuill({
    modules: moduleOptions,
    placeholder: placeholder,
    theme: "snow",
  });
  React.useEffect(() => {
    if (quill) {
      quill.clipboard.dangerouslyPasteHTML(value);
      quill.on("text-change", () => {
        handleChange(quill.root.innerHTML);
      });
    }
  }, [quill]);
  return (
    <div className="h-full ql-editor">
      <div ref={quillRef} />
    </div>
  );
};

export default function ExternalTextEditor({
  value,
  handleChange,
  placeholder,
  label,
  required = false,
  disabled = false,
}: {
  value: string;
  handleChange: (value: string) => void;
  placeholder: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}) {
  const [displayTab, setDisplayTab] = React.useState<
    "Editor" | "HTML" | "Preview"
  >(disabled ? "Preview" : "Editor");

  React.useEffect(() => {
    if (disabled) {
      setDisplayTab("Preview");
    } else {
      setDisplayTab("Editor");
    }
  }, [disabled]);

  const renderComponent = (componentType: "Editor" | "HTML" | "Preview") => {
    switch (componentType) {
      case "Editor": {
        return (
          <QuillComponent
            value={value}
            handleChange={handleChange}
            placeholder={placeholder}
          />
        );
      }
      case "HTML": {
        return (
          <Textarea
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            rows={6}
            placeholder="Before jumping in to a tough conversation..."
          />
        );
      }
      case "Preview": {
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: value,
            }}
            className="ql-editor rounded-md px-4 pt-4"
          />
        );
      }
      default: {
        const _exhaustiveCheck: never = componentType;
        throw new Error(`Unhandled component type: ${_exhaustiveCheck}`);
      }
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        {label && (
          <label className="font-bold">
            {label}
            {required && <span className="text-red-500"> *</span>}
          </label>
        )}
        <div className="flex items-center">
          <Button
            onClick={() => setDisplayTab("Editor")}
            variant={
              displayTab === "Editor" || disabled ? "primary" : "secondary"
            }
            className="rounded-l-md rounded-r-none"
            disabled={disabled}
          >
            Editor
          </Button>
          <Button
            onClick={() => setDisplayTab("Preview")}
            variant={
              displayTab === "Preview" || disabled ? "primary" : "secondary"
            }
            className="rounded-none border-l-0 border-r-0"
          >
            Preview
          </Button>
          <Button
            onClick={() => setDisplayTab("HTML")}
            variant={
              displayTab === "HTML" || disabled ? "primary" : "secondary"
            }
            className="rounded-r-md rounded-l-none"
            disabled={disabled}
          >
            HTML
          </Button>
        </div>
      </div>
      {renderComponent(displayTab)}
    </div>
  );
}
